import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService } from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";

export interface FilterItem {
  id: string | number;
  label: string;
  badgeValue?: string | number;
  iconClass?: string;
  iconWidth?: number;
  iconHeight?: number;
  badgeClass?: string;
  badgeSrOnly?: boolean;
  children?: FilterItem[];
  selected?: boolean;
  [key: string]: any;
}

export interface SkeletonConfig {
  type?: "line" | "circle" | "rect";
  number?: number;
  extraContainerClass?: string;
  useRandomWidth?: boolean;
  height?: string | number;
}

export interface FilterContentConfig {
  showBadges?: boolean; // Defaults to true if items have badgeValue
  showIcons?: boolean; // Defaults to false
  iconKey?: string; // Property name for icon identifier
  iconType?: "svg" | "image";
  iconPath?: string; // Base path for image icons
  itemClass?: string;
  nestedItemClass?: string;
  labelFormatter?: (item: FilterItem) => string;
  itemCondition?: (item: FilterItem) => boolean;
  skeletonConfig?: SkeletonConfig;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-filter-content",
  templateUrl: "filter-content.component.html",
  standalone: false,
})
export class FuiFilterContentComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {
  @Input() items: FilterItem[] = [];
  @Input() selectedItem: FilterItem;
  @Input() loading: boolean = false;
  @Input() config: FilterContentConfig = {};

  @Output() itemSelected: EventEmitter<FilterItem> = new EventEmitter<FilterItem>();

  public translatedItems: FilterItem[] = [];

  public showBadges: boolean = false;
  public showIcons: boolean = false;
  public iconKey: string = "";
  public iconType: "svg" | "image" = "image";
  public iconPath: string = "";
  public nestedItemsKey: string = "children";
  public itemClass: string = "fui-primary-category";
  public nestedItemClass: string = "fui-secondary-category";
  public labelFormatter: (item: FilterItem) => string;
  public itemCondition: (item: FilterItem) => boolean;
  public skeletonConfig: SkeletonConfig = { type: "line", extraContainerClass: "m-y-30", useRandomWidth: true, number: 10 };

  constructor(
    userService: UserService,
    private translate: TranslateService
  ) {
    super(userService);
    this.updateConfigVariables();
  }

  ngOnInit() {
    this.updateConfigVariables();
    this.updateTranslations();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.config || changes.items) {
      this.updateConfigVariables();
    }
    // Only rebuild `translatedItems` when something that actually affects the rendered list
    // changed. A caller passing an inline object literal / getter for `[selectedItem]` hands
    // us a new reference on every change-detection pass; without this guard that rebuilt the
    // whole list every pass, and (combined with the `track` below) re-created every link in
    // the DOM — which silently eats clicks whenever a CD tick lands between mousedown and
    // mouseup.
    const selectionChanged = changes.selectedItem && changes.selectedItem.previousValue?.id !== changes.selectedItem.currentValue?.id;
    if (changes.items || selectionChanged) {
      this.updateTranslations();
    }
  }

  /**
   * Identity for `@for (... ; track ...)`. `updateTranslations()` always produces brand-new
   * item objects, so tracking by object identity would tear down and re-create every row on
   * each rebuild. Tracking by id keeps the DOM nodes (and any in-flight click) alive.
   */
  public trackFilterItem = (index: number, item: FilterItem): string | number => {
    return item?.id ?? index;
  };

  private updateConfigVariables() {
    if (this.config.showBadges !== undefined) {
      this.showBadges = this.config.showBadges;
    } else {
      this.showBadges = this.items?.some((item) => item.badgeValue !== undefined) || false;
    }
    if (this.config.showIcons !== undefined) {
      this.showIcons = this.config.showIcons;
    } else {
      this.showIcons = !!(this.config.iconKey || this.config.iconPath);
    }
    this.iconKey = this.config.iconKey || "";
    this.iconType = this.config.iconType || "image";
    this.iconPath = this.config.iconPath || "";
    this.itemClass = this.config.itemClass || "fui-primary-category";
    this.nestedItemClass = this.config.nestedItemClass || "fui-secondary-category";
    this.labelFormatter = this.config.labelFormatter;
    this.itemCondition = this.config.itemCondition;
    this.skeletonConfig = this.config.skeletonConfig || { type: "line", extraContainerClass: "m-y-30", useRandomWidth: true, number: 10 };
  }

  private translateString(keyOrText: string): string {
    if (!keyOrText) {
      return keyOrText;
    }

    const translated = this.translate.instant(keyOrText);
    return translated !== keyOrText ? translated : keyOrText;
  }

  private updateTranslations() {
    if (!this.items?.length) {
      this.translatedItems = [];
      return;
    }

    this.translatedItems = this.items.map((item) => this.translateItem(item));
  }

  private translateItem(item: FilterItem): FilterItem {
    const translatedItem = { ...item };

    if (item.label) {
      translatedItem.label = this.translateString(item.label);
    }

    translatedItem["_isSelected"] = this.isSelected(item);
    translatedItem["_iconId"] = this.getIconId(item);
    translatedItem["_iconPath"] = this.getIconPath(item);
    translatedItem["_label"] = this.getItemLabel(translatedItem);
    translatedItem["_badgeValue"] = this.getBadgeValue(item);

    if (item.children?.length) {
      translatedItem.children = item.children.map((child) => this.translateItem(child));
    }

    return translatedItem;
  }

  isSelected(item: FilterItem): boolean {
    if (!this.selectedItem) return false;
    return this.selectedItem.id === item.id;
  }

  onItemClick(item: FilterItem): void {
    this.itemSelected.emit(item);
  }

  getItemLabel(item: FilterItem): string {
    if (this.labelFormatter) {
      return this.labelFormatter(item);
    }

    // Handle labels with prefixes (like # for channels)
    const label = item.label || "";
    if (label.startsWith("#")) {
      const prefix = "#";
      const labelWithoutPrefix = label.substring(1);
      const translatedLabel = this.translateString(labelWithoutPrefix);
      return prefix + translatedLabel;
    }

    // Handle complex labels that might have both static text and translation keys
    // Look for patterns like "Fender Play sphere.account.membership_billing"
    const parts = label.split(" ");
    if (parts.length > 1) {
      const translatedParts = parts.map((part) => {
        if (part.includes(".") && part.length > 3) {
          return this.translateString(part);
        }
        return part;
      });
      return translatedParts.join(" ");
    }

    // Use smart translation on the standardized label property
    return this.translateString(label);
  }

  getBadgeValue(item: FilterItem): string | number | null {
    if (!this.showBadges) return null;
    return item.badgeValue || null;
  }

  getIconId(item: FilterItem): string | null {
    if (!this.showIcons || !this.iconKey) return null;
    return item[this.iconKey] || null;
  }

  getIconPath(item: FilterItem): string | null {
    if (!this.showIcons || this.iconType !== "image" || !this.iconPath) return null;
    const iconName = item[this.iconKey] || item.title;
    if (!iconName) return null;

    let fileName: string;
    if (this.iconPath.includes("exchange")) {
      // Exchange icons follow the pattern: {name}-category.png
      fileName = iconName.includes(".") ? iconName : `${iconName}-category.png`;
    } else {
      // Default: just add .png extension if not present
      fileName = iconName.includes(".") ? iconName : `${iconName}.png`;
    }
    return `${this.iconPath}${fileName}`;
  }

  shouldShowItem(item: FilterItem): boolean {
    if (this.itemCondition) {
      return this.itemCondition(item);
    }
    return true;
  }
}
